/**
 * Catálogo declarativo de países soportados por ALTENOT.
 * Agregar un país requiere una entrada aquí; no contiene lógica de negocio.
 */

import type { CountryCode, CountryConfig } from "./types";

const digitalMethods = (
  first: string,
  second: string,
): CountryConfig["defaultDigitalMethods"] => [
  { slotId: "digital_1", name: first },
  { slotId: "digital_2", name: second },
];

export const COUNTRY_CONFIGS: Readonly<Record<CountryCode, CountryConfig>> = {
  AR: {
    code: "AR",
    name: "Argentina",
    locale: "es-AR",
    defaultLanguage: "es",
    currency: {
      code: "ARS",
      symbol: "$",
      name: "Peso argentino",
      decimalPlaces: 0,
    },
    bills: [20_000, 10_000, 2_000, 1_000, 500, 200],
    coins: [10, 5],
    smallestPhysicalUnit: 5,
    roundingRule: "nearestSmallestUnit",
    defaultDigitalMethods: digitalMethods("Mercado Pago", "MODO"),
    legalNotes: [],
  },
  AU: {
    code: "AU",
    name: "Australia",
    locale: "en-AU",
    defaultLanguage: "en",
    currency: {
      code: "AUD",
      symbol: "$",
      name: "Dólar australiano",
      decimalPlaces: 2,
    },
    bills: [100, 50, 20, 10, 5],
    coins: [2, 1, 0.5, 0.2, 0.1, 0.05],
    smallestPhysicalUnit: 0.05,
    roundingRule: "nearestSmallestUnit",
    defaultDigitalMethods: digitalMethods("Eftpos", "Beem"),
    legalNotes: ["El efectivo se redondea a cinco centavos."],
  },
  BR: {
    code: "BR",
    name: "Brasil",
    locale: "pt-BR",
    defaultLanguage: "pt",
    currency: {
      code: "BRL",
      symbol: "R$",
      name: "Real brasileño",
      decimalPlaces: 2,
    },
    bills: [200, 100, 50, 20, 10, 5, 2],
    coins: [1, 0.5, 0.25, 0.1, 0.05],
    smallestPhysicalUnit: 0.05,
    roundingRule: "nearestSmallestUnit",
    defaultDigitalMethods: digitalMethods("Pix", "PicPay"),
    legalNotes: [],
  },
  CA: {
    code: "CA",
    name: "Canadá",
    locale: "en-CA",
    defaultLanguage: "en",
    currency: {
      code: "CAD",
      symbol: "$",
      name: "Dólar canadiense",
      decimalPlaces: 2,
    },
    bills: [100, 50, 20, 10, 5],
    coins: [2, 1, 0.5, 0.25, 0.1, 0.05],
    smallestPhysicalUnit: 0.05,
    roundingRule: "nearestSmallestUnit",
    defaultDigitalMethods: digitalMethods("Interac Flash", "Apple Pay"),
    legalNotes: ["No existe moneda de un centavo."],
  },
  CH: {
    code: "CH",
    name: "Suiza",
    locale: "de-CH",
    defaultLanguage: "en",
    currency: {
      code: "CHF",
      symbol: "CHF",
      name: "Franco suizo",
      decimalPlaces: 2,
    },
    bills: [1_000, 200, 100, 50, 20, 10],
    coins: [5, 2, 1, 0.5, 0.2, 0.1, 0.05],
    smallestPhysicalUnit: 0.05,
    roundingRule: "nearestSmallestUnit",
    defaultDigitalMethods: digitalMethods("TWINT", "Apple Pay"),
    legalNotes: [],
  },
  CL: {
    code: "CL",
    name: "Chile",
    locale: "es-CL",
    defaultLanguage: "es",
    currency: {
      code: "CLP",
      symbol: "$",
      name: "Peso chileno",
      decimalPlaces: 0,
    },
    bills: [20_000, 10_000, 5_000, 2_000, 1_000],
    coins: [500, 100, 50, 10],
    smallestPhysicalUnit: 10,
    roundingRule: "nearestSmallestUnit",
    defaultDigitalMethods: digitalMethods("MACH", "CuentaRUT"),
    legalNotes: ["Aplica redondeo de efectivo."],
  },
  CN: {
    code: "CN",
    name: "China",
    locale: "zh-CN",
    defaultLanguage: "en",
    currency: {
      code: "CNY",
      symbol: "¥",
      name: "Yuan renminbi",
      decimalPlaces: 2,
    },
    bills: [100, 50, 20, 10, 5, 1],
    coins: [1, 0.5, 0.1],
    smallestPhysicalUnit: 0.1,
    roundingRule: "nearestSmallestUnit",
    defaultDigitalMethods: digitalMethods("Alipay", "WeChat Pay"),
    legalNotes: [],
  },
  CO: {
    code: "CO",
    name: "Colombia",
    locale: "es-CO",
    defaultLanguage: "es",
    currency: {
      code: "COP",
      symbol: "$",
      name: "Peso colombiano",
      decimalPlaces: 0,
    },
    bills: [100_000, 50_000, 20_000, 10_000, 5_000, 2_000],
    coins: [1_000, 500, 200, 100, 50],
    smallestPhysicalUnit: 50,
    roundingRule: "nearestSmallestUnit",
    defaultDigitalMethods: digitalMethods("Nequi", "Daviplata"),
    legalNotes: ["No se puede negar el cambio al cliente."],
  },
  CR: {
    code: "CR",
    name: "Costa Rica",
    locale: "es-CR",
    defaultLanguage: "es",
    currency: {
      code: "CRC",
      symbol: "₡",
      name: "Colón costarricense",
      decimalPlaces: 0,
    },
    bills: [20_000, 10_000, 5_000, 2_000, 1_000],
    coins: [500, 100, 50, 25, 10, 5],
    smallestPhysicalUnit: 5,
    roundingRule: "nearestSmallestUnit",
    defaultDigitalMethods: digitalMethods("SINPE Móvil", "Wink"),
    legalNotes: [],
  },
  ES: {
    code: "ES",
    name: "España",
    locale: "es-ES",
    defaultLanguage: "es",
    currency: { code: "EUR", symbol: "€", name: "Euro", decimalPlaces: 2 },
    bills: [500, 200, 100, 50, 20, 10, 5],
    coins: [2, 1, 0.5, 0.2, 0.1, 0.05, 0.02, 0.01],
    smallestPhysicalUnit: 0.01,
    roundingRule: "nearestSmallestUnit",
    defaultDigitalMethods: digitalMethods("Bizum", "PayPal"),
    legalNotes: [],
  },
  GB: {
    code: "GB",
    name: "Reino Unido",
    locale: "en-GB",
    defaultLanguage: "en",
    currency: {
      code: "GBP",
      symbol: "£",
      name: "Libra esterlina",
      decimalPlaces: 2,
    },
    bills: [50, 20, 10, 5],
    coins: [2, 1, 0.5, 0.2, 0.1, 0.05, 0.02, 0.01],
    smallestPhysicalUnit: 0.01,
    roundingRule: "nearestSmallestUnit",
    defaultDigitalMethods: digitalMethods("Apple Pay", "Faster Payments"),
    legalNotes: [],
  },
  GT: {
    code: "GT",
    name: "Guatemala",
    locale: "es-GT",
    defaultLanguage: "es",
    currency: { code: "GTQ", symbol: "Q", name: "Quetzal", decimalPlaces: 2 },
    bills: [200, 100, 50, 20, 10, 5, 1],
    coins: [1, 0.5, 0.25, 0.1, 0.05, 0.01],
    smallestPhysicalUnit: 0.01,
    roundingRule: "nearestSmallestUnit",
    defaultDigitalMethods: digitalMethods("Fri", "Tigo Money"),
    legalNotes: [],
  },
  IN: {
    code: "IN",
    name: "India",
    locale: "en-IN",
    defaultLanguage: "en",
    currency: {
      code: "INR",
      symbol: "₹",
      name: "Rupia india",
      decimalPlaces: 0,
    },
    bills: [500, 200, 100, 50, 20, 10],
    coins: [20, 10, 5, 2, 1],
    smallestPhysicalUnit: 1,
    roundingRule: "nearestSmallestUnit",
    defaultDigitalMethods: digitalMethods("PhonePe", "Google Pay"),
    legalNotes: [],
  },
  JP: {
    code: "JP",
    name: "Japón",
    locale: "ja-JP",
    defaultLanguage: "en",
    currency: { code: "JPY", symbol: "¥", name: "Yen", decimalPlaces: 0 },
    bills: [10_000, 5_000, 1_000],
    coins: [500, 100, 50, 10, 5, 1],
    smallestPhysicalUnit: 1,
    roundingRule: "nearestSmallestUnit",
    defaultDigitalMethods: digitalMethods("PayPay", "Suica"),
    legalNotes: [],
  },
  MX: {
    code: "MX",
    name: "México",
    locale: "es-MX",
    defaultLanguage: "es",
    currency: {
      code: "MXN",
      symbol: "$",
      name: "Peso mexicano",
      decimalPlaces: 2,
    },
    bills: [1_000, 500, 200, 100, 50, 20],
    coins: [20, 10, 5, 2, 1, 0.5],
    smallestPhysicalUnit: 0.5,
    roundingRule: "nearestSmallestUnit",
    defaultDigitalMethods: digitalMethods("Mercado Pago", "Spin by Oxxo"),
    legalNotes: [],
  },
  PA: {
    code: "PA",
    name: "Panamá",
    locale: "es-PA",
    defaultLanguage: "es",
    currency: { code: "PAB", symbol: "B/.", name: "Balboa", decimalPlaces: 2 },
    bills: [100, 50, 20, 10, 5, 1],
    coins: [1, 0.5, 0.25, 0.1, 0.05, 0.01],
    smallestPhysicalUnit: 0.01,
    roundingRule: "nearestSmallestUnit",
    defaultDigitalMethods: digitalMethods("Yappy", "Nequi Panamá"),
    legalNotes: ["Los billetes en circulación son dólares estadounidenses."],
  },
  PE: {
    code: "PE",
    name: "Perú",
    locale: "es-PE",
    defaultLanguage: "es",
    currency: { code: "PEN", symbol: "S/", name: "Sol", decimalPlaces: 2 },
    bills: [200, 100, 50, 20, 10],
    coins: [5, 2, 1, 0.5, 0.2, 0.1],
    smallestPhysicalUnit: 0.1,
    roundingRule: "nearestSmallestUnit",
    defaultDigitalMethods: digitalMethods("Yape", "Plin"),
    legalNotes: [],
  },
  US: {
    code: "US",
    name: "Estados Unidos",
    locale: "en-US",
    defaultLanguage: "en",
    currency: {
      code: "USD",
      symbol: "$",
      name: "Dólar estadounidense",
      decimalPlaces: 2,
    },
    bills: [100, 50, 20, 10, 5, 2, 1],
    coins: [1, 0.5, 0.25, 0.1, 0.05, 0.01],
    smallestPhysicalUnit: 0.01,
    roundingRule: "nearestSmallestUnit",
    defaultDigitalMethods: digitalMethods("Zelle", "Venmo"),
    legalNotes: [],
  },
};

export const COUNTRY_OPTIONS = Object.values(COUNTRY_CONFIGS).sort(
  (left, right) => left.name.localeCompare(right.name, "es"),
);
